/**
 * Deterministic Grade ID Generator
 * 
 * Generates globally unique, deterministic grade IDs that enable idempotent writes.
 * Same grade written multiple times = same document (no duplicates).
 * 
 * ID Format (with messageId for test-retests):
 * {conversationId}_{promptId}_{graderModelId}_{candidateModelId}_{type}_{messageId}
 * 
 * ID Format (without messageId for backward compatibility):
 * {conversationId}_{promptId}_{graderModelId}_{candidateModelId}_{type}
 * 
 * Where:
 * - conversationId: Globally unique Firestore doc ID
 * - promptId: User prompt message ID
 * - graderModelId: Model that gave the grade (sanitized)
 * - candidateModelId: Model being graded (sanitized)
 * - type: 'SE' (self-eval) or 'PR' (peer-review)
 * - messageId: (Optional) Grading message ID for test-retests
 */

/**
 * Sanitize model ID for use in document ID
 * Firestore doc IDs can't contain: / \ . # [ ] * 
 */
function sanitizeForDocId(modelId: string): string {
  return modelId
    .replace(/\//g, '_')
    .replace(/\\/g, '_')
    .replace(/\./g, '_')
    .replace(/#/g, '_')
    .replace(/\[/g, '_')
    .replace(/\]/g, '_')
    .replace(/\*/g, '_');
}

/**
 * Generate deterministic grade ID
 * 
 * @param messageId - Optional grading message ID to differentiate test-retests
 *                   (same grader grading same receiver multiple times)
 */
export function generateDeterministicGradeId(
  conversationId: string,
  promptId: string,
  graderModelId: string,
  candidateModelId: string,
  isSelfEval: boolean,
  messageId?: string
): string {
  if (!conversationId || !promptId || !graderModelId || !candidateModelId) {
    throw new Error('All parameters required for deterministic grade ID');
  }
  
  const sanitizedGrader = sanitizeForDocId(graderModelId);
  const sanitizedCandidate = sanitizeForDocId(candidateModelId);
  const type = isSelfEval ? 'SE' : 'PR';
  
  // Include messageId if provided (for test-retests)
  if (messageId) {
    const sanitizedMessageId = sanitizeForDocId(messageId);
    return `${conversationId}_${promptId}_${sanitizedGrader}_${sanitizedCandidate}_${type}_${sanitizedMessageId}`;
  }
  
  return `${conversationId}_${promptId}_${sanitizedGrader}_${sanitizedCandidate}_${type}`;
}

/**
 * Parse grade ID back into components (for debugging/analysis)
 */
export function parseGradeId(gradeId: string): {
  conversationId: string;
  promptId: string;
  graderModelId: string;
  candidateModelId: string;
  isSelfEval: boolean;
} | null {
  const parts = gradeId.split('_');
  
  // Need at least 5 parts: convId, promptId, grader, candidate, type
  if (parts.length < 5) {
    return null;
  }
  
  // Last part is type
  const type = parts[parts.length - 1];
  const isSelfEval = type === 'SE';
  
  // Second to last could be part of candidateModelId, need to find where candidate starts
  // This is tricky because model IDs themselves contain underscores after sanitization
  // For now, we'll use a simpler approach: just store the raw ID
  
  // Simplified: Can't reliably parse back due to underscores in model IDs
  // But that's okay - we only need this for debugging
  return null;
}

